import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowUpRight } from "lucide-react";
import { getCategories, getProducts } from "@/lib/api";
import { IMAGES } from "@/lib/data";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("category") || "";
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { getCategories().then(setCats).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    getProducts({ category: activeCat || undefined, search: search || undefined })
      .then(setProducts).finally(() => setLoading(false));
  }, [activeCat, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const setCat = (slug) => {
    if (slug) params.set("category", slug); else params.delete("category");
    setParams(params);
  };

  return (
    <main className="bg-beige" data-testid="products-page">
      {/* Header */}
      <section className="relative pt-40 pb-20 bg-forest text-beige overflow-hidden grain">
        <img src={IMAGES.spicesMacro} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest to-forest/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <p className="overline text-gold mb-4">The Portfolio</p>
          <h1 className="font-serif text-6xl md:text-7xl leading-none">Products of the Earth,<br /><span className="italic text-gold">graded for the world.</span></h1>
        </div>
      </section>

      {/* Controls */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-6 justify-between items-start lg:items-center mb-10 sticky top-20 bg-beige/90 backdrop-blur-md z-20 py-3">
          <div className="flex flex-wrap gap-2">
            <button data-testid="filter-all" onClick={() => setCat("")}
              className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${!activeCat ? "bg-forest text-beige border-forest" : "border-forest/30 text-forest hover:border-forest"}`}>
              All
            </button>
            {cats.map((c) => (
              <button key={c.slug} data-testid={`filter-${c.slug}`} onClick={() => setCat(c.slug)}
                className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${activeCat === c.slug ? "bg-forest text-beige border-forest" : "border-forest/30 text-forest hover:border-forest"}`}>
                {c.name}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 border-b border-forest/30 focus-within:border-gold min-w-[220px]">
            <Search className="w-4 h-4 text-forest/50" />
            <input data-testid="product-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products…" className="bg-transparent py-2 text-sm outline-none flex-1 text-forest placeholder:text-forest/40" />
          </div>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (<div key={i} className="h-72 bg-forest/5 animate-pulse" />))}
          </div>
        ) : products.length === 0 ? (
          <p className="text-center py-24 font-serif text-3xl text-forest/50">No products found.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="product-grid">
            {products.map((p, i) => (
              <motion.div key={p.slug}
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: (i % 8) * 0.05 }}>
                <Link to={`/products/${p.slug}`} data-testid={`product-${p.slug}`} className="group block">
                  <div className="relative overflow-hidden h-64 mb-4 rounded-2xl">
                    <img src={p.image} alt={`Organic ${p.name} — Karmaa Overseas exporter India`} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-forest/0 group-hover:bg-forest/20 transition-colors" />
                    <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                      {p.organic && <span className="text-[9px] uppercase tracking-wider font-semibold bg-olive text-beige px-2.5 py-1 rounded-full">Organic</span>}
                      <span className="text-[9px] uppercase tracking-wider font-semibold bg-gold text-forest px-2.5 py-1 rounded-full">Export Available</span>
                    </div>
                    <span className="absolute top-4 right-4 w-9 h-9 bg-beige/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 -translate-y-2 group-hover:translate-y-0 transition-all">
                      <ArrowUpRight className="w-4 h-4 text-forest" />
                    </span>
                  </div>
                  <p className="overline text-gold text-[10px] mb-1">{p.category}</p>
                  <h3 className="font-serif text-2xl text-forest group-hover:text-gold transition-colors">{p.name}</h3>
                  {p.name_hi && <p className="text-ink/50 text-sm mt-0.5">{p.name_hi}</p>}
                  <div className="mt-3 flex items-center gap-4">
                    <span className="text-xs uppercase tracking-widest text-forest border-b border-forest/40 pb-0.5 group-hover:border-gold group-hover:text-gold transition-colors">View Details</span>
                    <span className="text-xs uppercase tracking-widest text-olive">Request Quote</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
